import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { getUrl } from '@ngxp/rest';
import {
	VerfahrenFacade,
	VerfahrenLinkRel,
	VerfahrenMessages,
	VerfahrenResource,
	VerfahrenStammdaten,
	VerfahrenStatusLabel,
} from '@schir-int-client/verfahren-shared';
import { AmtlichenAusdruckErstellenDialogComponent } from '@schir-int-client/alternative-text';
import { DialogService, HandlesBackdropClickAndEscapeKey, NotizDialogComponent } from '@schir-int-client/tech';
import { isNil } from 'lodash-es';
import { Observable, Subscription } from 'rxjs';
import {
	AktenzeichenRenderer,
	AktenzeichenRendererProvider,
	AktenzeichenUtil,
} from '@schir-int-client/aktenzeichen-shared';
import { take } from 'rxjs/operators';
import { getId, getIdNumber } from '../../../../../test-helpers/src/lib/resource-helper';

@Component({
	selector: 'schir-int-client-verfahren-single',
	templateUrl: './verfahren-single.component.html',
	styleUrls: ['./verfahren-single.component.scss'],
	standalone: false,
})
export class VerfahrenSingleComponent
	extends HandlesBackdropClickAndEscapeKey<NotizDialogComponent | AmtlichenAusdruckErstellenDialogComponent>
	implements OnChanges, OnDestroy
{
	@Input() verfahren: VerfahrenResource;

	private subscriptions: Subscription[] = [];

	isSelected: Observable<boolean>;
	verfahrenStatusLabel = VerfahrenStatusLabel;
	private renderer: AktenzeichenRenderer;

	constructor(
		private facade: VerfahrenFacade,
		private router: Router,
		protected dialogService: DialogService,
		rendererProvider: AktenzeichenRendererProvider
	) {
		super(dialogService);

		this.renderer = rendererProvider.getRenderer();
	}

	ngOnChanges() {
		this.isSelected = this.facade.isVerfahrenSelected(this.verfahren);
	}

	ngOnDestroy() {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	openAmtlichenAuszugErstellenDialog() {
		this.selectVerfahren();
		this.matDialogRef = this.dialogService.openEditorDialog(this, AmtlichenAusdruckErstellenDialogComponent);
	}

	downloadSchiffszertifikat() {
		this.selectVerfahren();
		this.facade.downloadSchiffszertifikat();
	}

	downloadSchiffsbrief() {
		this.selectVerfahren();
		this.facade.downloadSchiffsbrief();
	}

	openRegisterBlatt() {
		this.router.navigate(['/verfahrenDetail/', getId(this.verfahren)]);
	}

	selectVerfahren() {
		this.facade.setSelectedVerfahren(this.verfahren);
	}

	openVerfahren($event: MouseEvent = null) {
		if ($event?.ctrlKey && $event?.altKey) {
			this.exportVerfahren();
			return;
		}

		this.selectVerfahren();

		this.router.navigate(['/verfahren/', getId(this.verfahren)]);
	}

	markAsAbgelehnt() {
		this.facade.markAsAbgelehnt(this.verfahren);
	}

	openNotizDialog() {
		const aktenzeichen = AktenzeichenUtil.parse(this.verfahren);
		const aktenzeichenRendered = this.renderer.render(aktenzeichen);

		const title = 'Notizen zum Verfahren ' + aktenzeichenRendered;

		this.matDialogRef = this.dialogService.openEditorDialog(this, NotizDialogComponent, {
			data: {
				title: title,
				notiz: this.verfahren.notiz,
			},
		});

		const editor = <NotizDialogComponent>this.matDialogRef.componentInstance;
		this.subscriptions.push(
			editor.notizEvent.pipe(take(1)).subscribe(data => {
				this.updateNotiz(data);
			})
		);
	}

	updateNotiz(notiz: string) {
		this.facade.updateNotiz(notiz, getUrl(this.verfahren, VerfahrenLinkRel.NOTIZ));
	}

	deleteVerfahren() {
		this.facade.delete(this.verfahren);
	}

	getSchiffName(): string {
		let schiffsName: string = '';

		if (!isNil(this.verfahren.stammdaten.schiffName)) {
			schiffsName += this.verfahren.stammdaten.schiffName;
		}
		if (!isNil(this.verfahren.stammdaten.unsignedSchiffName)) {
			schiffsName += '(' + this.verfahren.stammdaten.unsignedSchiffName + ')';
		}

		return schiffsName;
	}

	get verfahrenId(): number {
		return getIdNumber(this.verfahren);
	}

	get stammdaten(): VerfahrenStammdaten {
		return this.verfahren.stammdaten;
	}

	get aktenzeichen() {
		const aktenzeichen = AktenzeichenUtil.parse(this.verfahren.aktenzeichen);
		return this.renderer.render(aktenzeichen).split(' ').join('_');
	}

	openUsignalZuordnenDialog() {
		const aktenzeichen = AktenzeichenUtil.parse(this.verfahren);
		const aktenzeichenRendered = this.renderer.render(aktenzeichen);
		this.subscriptions.push(
			this.dialogService
				.openDialogYesNo(
					VerfahrenMessages.CONFIRM_ASSIGN_USIGNAL.replace('{aktenzeichen}', aktenzeichenRendered)
				)
				.pipe(take(1))
				.subscribe(data => {
					if (data.agree) {
						this.facade.assignUSignal(this.verfahren);
					}
				})
		);
	}

	private exportVerfahren() {
		const url: string = this.verfahren._links[VerfahrenLinkRel.EXPORT_VERFAHREN].href;
		this.facade.exportVerfahren(url);
	}
}
